
	/*!
  * tiptap vundefined
  * (c) 2018 Scrumpy UG (limited liability)
  * @license MIT
  */

import { InputRule, wrappingInputRule, textblockTypeInputRule } from 'prosemirror-inputrules';
export { wrappingInputRule, textblockTypeInputRule } from 'prosemirror-inputrules';
import { setBlockType, chainCommands, deleteSelection, joinBackward, selectNodeBackward, joinForward, selectNodeForward, joinUp, joinDown, lift, newlineInCode, exitCode, createParagraphNear, liftEmptyBlock, splitBlock, splitBlockKeepMarks, selectParentNode, selectAll, wrapIn, toggleMark, autoJoin, baseKeymap, pcBaseKeymap, macBaseKeymap } from 'prosemirror-commands';
export { chainCommands, deleteSelection, joinBackward, selectNodeBackward, joinForward, selectNodeForward, joinUp, joinDown, lift, newlineInCode, exitCode, createParagraphNear, liftEmptyBlock, splitBlock, splitBlockKeepMarks, selectParentNode, selectAll, wrapIn, setBlockType, toggleMark, autoJoin, baseKeymap, pcBaseKeymap, macBaseKeymap } from 'prosemirror-commands';
import { nodeIsActive } from 'tiptap-utils';
import { wrapInList, liftListItem, addListNodes, splitListItem, sinkListItem } from 'prosemirror-schema-list';
export { addListNodes, wrapInList, splitListItem, liftListItem, sinkListItem } from 'prosemirror-schema-list';

function markInputRule (regexp, markType, getAttrs) {
	return new InputRule(regexp, function (state, match, start, end) {
		var attrs = getAttrs instanceof Function ? getAttrs(match) : getAttrs;
		var tr = state.tr;

		if (match[1]) {
			var startSpaces = match[0].search(/\S/);
			var textStart = start + match[0].indexOf(match[1]);
			var textEnd = textStart + match[1].length;
			if (textEnd < end) {
				tr.delete(textEnd, end);
			}
			if (textStart > start) {
				tr.delete(start + startSpaces, textStart);
			}
			end = start + startSpaces + match[1].length;
		}

		tr.addMark(start, end, markType.create(attrs));
		tr.removeStoredMark(markType); // Do not continue with mark.
		return tr
	})
}

function removeMark (type) {
  return function (state, dispatch) {
		var ref = state.selection;
		var from = ref.from;
		var to = ref.to;
		return dispatch(state.tr.removeMark(from, to, type))
	}
}

function replaceText (range, type, attrs) {
	if ( attrs === void 0 ) attrs = {};

	return function (state, dispatch) {
		var ref = state.selection;
		var $from = ref.$from;
		var index = $from.index();

		if (!$from.parent.canReplaceWith(index, index, type)) {
			return false
		}

		if (dispatch) {
			dispatch(state.tr.replaceWith(range.from, range.to, type.create(attrs)));
		}

		return true
	}
}

function setInlineBlockType (type, attrs) {
	if ( attrs === void 0 ) attrs = {};

	return function (state, dispatch) {
		var ref = state.selection;
		var $from = ref.$from;
		var index = $from.index();

		if (!$from.parent.canReplaceWith(index, index, type)) {
			return false
		}

		if (dispatch) {
			dispatch(state.tr.replaceSelectionWith(type.create(attrs)));
		}

		return true
	}
}

// this is a copy of canSplit
// see https://github.com/ProseMirror/prosemirror-transform/blob/master/src/structure.js

function canSplit(doc, pos, depth, typesAfter) {
  if ( depth === void 0 ) depth = 1;

  var $pos = doc.resolve(pos), base = $pos.depth - depth;
  var innerType = (typesAfter && typesAfter[typesAfter.length - 1]) || $pos.parent;
  if (base < 0 || $pos.parent.type.spec.isolating ||
      !$pos.parent.canReplace($pos.index(), $pos.parent.childCount) ||
      !innerType.type.validContent($pos.parent.content.cutByIndex($pos.index(), $pos.parent.childCount)))
    { return false }
  for (var d = $pos.depth - 1, i = depth - 2; d > base; d--, i--) {
    var node = $pos.node(d), index$1 = $pos.index(d);
    if (node.type.spec.isolating) { return false }
    var rest = node.content.cutByIndex(index$1, node.childCount);
    var after = (typesAfter && typesAfter[i]) || node;
		if (after != node) { rest = rest.replaceChild(0, after.type.create(after.attrs)); }

		/* Change starts from here */
		// if (!node.canReplace(index + 1, node.childCount) || !after.type.validContent(rest))
    //   return false
    if (!node.canReplace(index$1 + 1, node.childCount))
      { return false }
		/* Change ends here */
  }
  var index = $pos.indexAfter(base);
  var baseType = typesAfter && typesAfter[0];
  return $pos.node(base).canReplaceWith(index, index, baseType ? baseType.type : $pos.node(base + 1).type)
}

// this is a copy of splitListItem
// see https://github.com/ProseMirror/prosemirror-schema-list/blob/master/src/schema-list.js

function splitToDefaultListItem (itemType) {
  return function(state, dispatch) {
    var ref = state.selection;
    var $from = ref.$from;
    var $to = ref.$to;
    var node = ref.node;
    if ((node && node.isBlock) || $from.depth < 2 || !$from.sameParent($to)) { return false }
    var grandParent = $from.node(-1);
    if (grandParent.type != itemType) { return false }
    if ($from.parent.content.size == 0) {
      // In an empty block. If this is a nested list, the wrapping
      // list item should be split. Otherwise, bail out and let next
      // command handle lifting.
      if ($from.depth == 2 || $from.node(-3).type != itemType ||
					$from.index(-2) != $from.node(-2).childCount - 1) { return false }

      if (dispatch) {
        var wrap = Fragment.empty, keepItem = $from.index(-1) > 0;
        // Build a fragment containing empty versions of the structure
        // from the outer list item to the parent node of the cursor
        for (var d = $from.depth - (keepItem ? 1 : 2); d >= $from.depth - 3; d--)
          { wrap = Fragment.from($from.node(d).copy(wrap)); }
        // Add a second list item with an empty default start node
				wrap = wrap.append(Fragment.from(itemType.createAndFill()));
        var tr$1 = state.tr.replace($from.before(keepItem ? null : -1), $from.after(-3), new Slice(wrap, keepItem ? 3 : 2, 2));
        tr$1.setSelection(state.selection.constructor.near(tr$1.doc.resolve($from.pos + (keepItem ? 3 : 2))));
        dispatch(tr$1.scrollIntoView());
      }
      return true
    }
		var nextType = $to.pos == $from.end() ? grandParent.contentMatchAt($from.indexAfter(-1)).defaultType : null;
		var tr = state.tr.delete($from.pos, $to.pos);

		/* Change starts from here */
		// let types = nextType && [null, {type: nextType}]
    var types = nextType && [{type: itemType}, {type: nextType}];
    if (!types) { types = [{type: itemType}, null]; }
		/* Change ends here */

		if (!canSplit(tr.doc, $from.pos, 2, types)) { return false }
    if (dispatch) { dispatch(tr.split($from.pos, 2, [{type: state.schema.nodes.todo_item, attrs: { done: false }}]).scrollIntoView()); }
    return true
  }
}

function toggleBlockType (type, toggletype, attrs) {
	if ( attrs === void 0 ) attrs = {};

	return function (state, dispatch, view) {
		var isActive = nodeIsActive(state, type, attrs);

		if (isActive) {
			return setBlockType(toggletype)(state, dispatch, view)
		}

		return setBlockType(type, attrs)(state, dispatch, view)
	}
}

function toggleList(type, itemType) {
  return function (state, dispatch, view) {
    var isActive = nodeIsActive(state, type);

    if (isActive) {
      return liftListItem(itemType)(state, dispatch, view)
    }

    return wrapInList(type)(state, dispatch, view)
  }
}

// https://discuss.prosemirror.net/t/list-type-toggle/948

// import { wrapInList, liftListItem } from 'prosemirror-schema-list'

// function isList(node, schema) {
//   return (node.type === schema.nodes.bullet_list || node.type === schema.nodes.ordered_list)
// }

// export default function toggleList(listType, schema) {
//   const lift = liftListItem(schema.nodes.list_item)
// 	const wrap = wrapInList(listType)

//   return (state, dispatch) => {
//     const { $from, $to } = state.selection
//     const range = $from.blockRange($to)
//     if (!range) {
//       return false
// 		}

//     if (range.depth >= 2 && $from.node(range.depth - 1).type === listType) {
//       return lift(state, dispatch)
//     } else if (range.depth >= 2 && isList($from.node(range.depth - 1), schema)) {
//       const tr = state.tr
// 			const node = $from.before(range.depth - 1)
// 			console.log({node})
//       // TODO: how do I pass the node above to `setNodeType`?
//       // tr.setNodeType(range.start, listType);
//       if (dispatch) dispatch(tr)
//       return false
//     } else {
//       return wrap(state, dispatch)
//     }
//   }
// }

function updateMark (type, attrs) {
  return function (state, dispatch) {
		var ref = state.selection;
		var from = ref.from;
		var to = ref.to;
		return dispatch(state.tr.addMark(from, to, type.create(attrs)))
	}
}

export { markInputRule, removeMark, replaceText, setInlineBlockType, splitToDefaultListItem, toggleBlockType, toggleList, updateMark };



//////////////////
// WEBPACK FOOTER
// ./node_modules/tiptap-commands/dist/commands.esm.js
// module id = null
// module chunks = 